import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { RootState, RootDispatch } from 'store/types';
import { getCurrentPosition } from 'store/geolocation/actions';
import { GeoCodeLocation } from 'services/LocationService/types';
import { Coordinate } from 'modules/types/common';
import { geoCodeLocation } from 'services/LocationService/GoogleApis';
import { addressFromComponents } from 'services/LocationService/utils';
import * as geolocationSelectors from 'store/geolocation/selectors';

import { MuiBox, MuiGrid, MuiTypography, MuiButton } from 'theme/material-ui';
import { GooglePlacesInput } from 'components/booking/LocationSearch';
import { Svg } from 'components/UI/Svg';

import { Color } from 'modules/styles/colors';
import { FontWeight, IconSize } from 'modules/styles/variables';

interface Props {
  handleAddressSelect: Function;
  geolocationAccepted: Function;
  geolocationCoords: Coordinate;
  getCurrentPosition: () => void;
}

const LocationSection = ({
  handleAddressSelect,
  geolocationAccepted,
  geolocationCoords,
  getCurrentPosition
}: Props) => {
  const [locationError, setLocationError] = useState<boolean>(false);
  const [useMyLocation, setUseMyLocation] = useState<boolean>(false);

  const updateAddress = (details: GeoCodeLocation) => {
    const address = addressFromComponents(details.address_components);
    const location = { name: 'location', value: details.geometry.location };
    const format = { name: 'formatted_address', value: details.formatted_address };
    return [...address, location, format];
  };

  const getGeolocationStateCode = (geolocation?: GeoCodeLocation) => {
    if (!geolocation) return '';
    const components = geolocation.address_components;
    const geolocationState = components.find(({ types }) => {
      return types[0] === 'administrative_area_level_1';
    });
    return geolocationState ? geolocationState.short_name : '';
  };

  function initLocation() {
    if (geolocationAccepted && geolocationCoords) {
      const lat = geolocationCoords.latitude;
      const lng = geolocationCoords.longitude;

      geoCodeLocation(`${lat},${lng}`)
        .then(res => {
          const geolocationStateCode = getGeolocationStateCode(res[0]);
          if (geolocationStateCode) {
            const address = addressFromComponents(res[0]?.address_components);
            const location = { name: 'location', value: res[0].geometry.location };
            const format = { name: 'formatted_address', value: res[0].formatted_address };

            handleAddressSelect([...address, location, format]);
            setUseMyLocation(true);
          }
        })
        .catch(e => {
          setLocationError(true);
          setUseMyLocation(false);
        });
    } else {
      setLocationError(true);
      setUseMyLocation(false);
    }
  }

  const clearMyLocation = () => {
    setUseMyLocation(false);
    // need to clear the location data too
    handleAddressSelect(null);
  };

  useEffect(() => {
    if (!geolocationAccepted && !geolocationCoords) getCurrentPosition();
  }, []);

  return (
    <MuiBox paddingTop={2}>
      <MuiGrid xs={12} container justify="space-between">
        <MuiGrid item xs>
          <MuiTypography gutterBottom variant="h6" fontWeight={FontWeight.normal}>
            Area <span style={{ color: 'red' }}>*</span>
          </MuiTypography>
        </MuiGrid>
        <MuiGrid item>
          <MuiButton
            size="medium"
            variant="text"
            color="primary"
            onClick={initLocation}
            disabled={!geolocationAccepted || locationError}
            startIcon={<Svg color={Color.secondary} name="DirectionIcon" size={IconSize.small} />}
            style={{ fontWeight: FontWeight.normal }}
          >
            My Location
          </MuiButton>
        </MuiGrid>
      </MuiGrid>
      <MuiGrid xs={12}>
        <MuiBox>
          <GooglePlacesInput
            useMyLocation={useMyLocation}
            placeholder="Street / City / Zip"
            clearMyLocation={clearMyLocation}
            hideIcon
            handleOnPress={(geoCode: GeoCodeLocation) => {
              if (geoCode) {
                const addressFieldValues = updateAddress(geoCode);
                handleAddressSelect(addressFieldValues);
              }
            }}
          />
        </MuiBox>
      </MuiGrid>
    </MuiBox>
  );
};

const mapStateToProps = (state: RootState) => ({
  geolocationAccepted: geolocationSelectors.geolocationAcceptedSelector(state),
  geolocationCoords: geolocationSelectors.geolocationCoordsSelector(state)
});

const mapDispatchToProps = (dispatch: RootDispatch) => ({
  getCurrentPosition: () => dispatch(getCurrentPosition())
});

export default connect(mapStateToProps, mapDispatchToProps)(LocationSection);
